'use client';

import { DestinationCGType, SearchResultCGType, SurveyCGType } from '@/types/graphql/root';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import DestinationMarkerSvg from './destination-marker-svg';
import { DayPinColor } from '@/utils';

type DestinationPosition = {
  day: number;
  pos: number;
  replace?: boolean;
  replaceDestId?: string;
};

export default function SelectDayForAddDestModal({
  useIsVisible: [isVisible, setIsVisible],
  selectedTempSearchResult,
  selectedSearchResult,
  setSelectedSearchResult,
  setAddDestinationsPosition,
  addDestinationsPosition,
  destinations,
  trip,
  onClickAddDestinations,
}: {
  useIsVisible: [boolean, React.Dispatch<React.SetStateAction<boolean>>];
  selectedTempSearchResult: SearchResultCGType | null;
  selectedSearchResult: SearchResultCGType | null;
  setSelectedSearchResult: React.Dispatch<React.SetStateAction<SearchResultCGType | null>>;
  setAddDestinationsPosition: React.Dispatch<React.SetStateAction<DestinationPosition | null>>;
  destinations: DestinationCGType[];
  trip: SurveyCGType;
  onClickAddDestinations: () => void;
  addDestinationsPosition: DestinationPosition | null;
}) {
  const [fadeType, setFadeType] = useState<'in' | 'out'>('in');

  useEffect(() => {
    if (!isVisible) {
      setFadeType('out');
    } else {
      setFadeType('in');
      const isNotHidden = document.body.style.overflowY !== 'hidden';
      if (isNotHidden) {
        document.body.style.overflowY = 'hidden';
      }

      return () => {
        if (isNotHidden) document.body.style.overflowY = 'auto';
      };
    }
  }, [isVisible]);

  useEffect(() => {
    if (addDestinationsPosition && !addDestinationsPosition.replace && selectedSearchResult) {
      onClickAddDestinations();
    }
  }, [addDestinationsPosition, onClickAddDestinations, selectedTempSearchResult]);

  const selectDayModalDayList = useMemo(() => {
    if (!trip.fromDateTime || !trip.toDateTime) return null;

    const fromDate = dayjs(trip.fromDateTime);
    const toDate = dayjs(trip.toDateTime);
    const duration = toDate.startOf('day').diff(fromDate.startOf('day'), 'day') + 1;
    if (duration <= 0) return null;

    const results: {
      dayNumber: number;
      dateString: string;
    }[] = [];
    for (let i = 1; i <= duration; i++) {
      results.push({
        dayNumber: i,
        dateString: fromDate
          .add(i - 1, 'day')
          .locale('ko')
          .format('MM.DD ddd'),
      });
    }

    return results;
  }, [trip]);

  const height = useMemo(() => {
    if (!isVisible || !selectDayModalDayList) return 0;

    return 24 + selectDayModalDayList.length * 52 + 24;
  }, [selectDayModalDayList, isVisible]);

  const onClickSelectDay = useCallback(
    (dayNumber: number) => {
      setIsVisible(false);

      if (!selectedTempSearchResult) return;

      const destinationsInDay = destinations
        .filter((_d) => _d.day === dayNumber && !_d.accommType)
        .sort((a, b) => a.pos - b.pos);

      const newPosition: DestinationPosition = {
        day: dayNumber,
        pos: destinationsInDay.length === 0 ? 1 : destinationsInDay[destinationsInDay.length - 1].pos + 1,
      };

      setAddDestinationsPosition(newPosition);
      setSelectedSearchResult(selectedTempSearchResult);
    },
    [
      selectedTempSearchResult,
      setAddDestinationsPosition,
      setSelectedSearchResult,
      destinations,
      addDestinationsPosition,
    ],
  );

  if (!isVisible) return null;

  return (
    <div
      className='fixed w-full max-w-480pxr top-0 z-[100] h-dvh overflow-hidden flex items-center justify-center'
      onClick={() => {
        setIsVisible(false);
      }}
    >
      <div className='absolute top-0 left-0 w-full h-full bg-custom-black/60 animate-[fadeIn_300ms_ease-in-out]'></div>
      <div
        className='poi-modal-in-main w-full max-w-480pxr bg-white fixed bottom-0 rounded-t-16pxr'
        style={{
          height,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='flex flex-col py-24pxr'>
          {selectDayModalDayList?.map(({ dayNumber, dateString }) => {
            let bgColor = DayPinColor.day1;
            if (dayNumber === 1) bgColor = DayPinColor.day1;
            else if (dayNumber === 2) bgColor = DayPinColor.day2;
            else if (dayNumber === 3) bgColor = DayPinColor.day3;
            else if (dayNumber === 4) bgColor = DayPinColor.day4;
            else if (dayNumber === 5) bgColor = DayPinColor.day5;
            else if (dayNumber === 6) bgColor = DayPinColor.day6;
            return (
              <button
                key={'daySelectButton_' + dayNumber}
                className='flex flex-row items-center justify-center border-b border-gray-200 gap-16pxr h-52pxr last:border-b-0'
                onClick={() => onClickSelectDay(dayNumber)}
              >
                <DestinationMarkerSvg bgColor={bgColor} />
                <span className={`button-16-m text-gray-700`}>{dayNumber}일차</span>
                <span className={`button-14-m text-gray-500`}>{dateString}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
